import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import { basket, useBasket, useBasketMeta, qtyStep, minQty } from '../utils/basketStore';
import { useFavoriteIds } from '../utils/favoritesStore';
import { useSearchSuggestions } from '../utils/useSearchSuggestions';
import SearchBox from './SearchBox';
import ProductRow from './ProductRow';
import ImportListDialog from './ImportListDialog';
import ShareListDialog from './ShareListDialog';
import ListHistoryDialog from './ListHistoryDialog';

// רשימת קניות: המחיר של הרשימה בכל חנות, והקנייה הכי משתלמת בפיצול בין החנויות.
// המחירים מ-spCompareBasket (נטען מחדש רק כשמוצר נוסף / הוסר); הסכומים מחושבים כאן — שינוי כמות מיידי.
// הרשימה עצמה בשרת (basketStore.js / ShoppingList.sql): שיתוף עם בני הבית, הוספה מהירה (מועדפים / רשימות קודמות),
// שליחה בוואטסאפ, ו"סיימתי" ששומר את הרשימה ב"רשימות קודמות". active — הטאב גלוי (MainPage).

const COMPARE_DEBOUNCE_MS = 300;
const PICKER_RESULTS = 8;

const formatPrice = (n) => `₪${Number(n || 0).toFixed(2)}`;
const formatQty = (item) => (item.isWeighable ? `${item.qty} ק"ג` : `${item.qty}`);

// סכום לכל חנות, החנות הזולה, והפיצול הכי משתלם
const computeComparison = (list, stores, pricesByItem) => {
  const priced = list.filter((x) => pricesByItem[x.id]);   // מוצרים שנמצאו בחנות אחת לפחות

  const perStore = stores.map((st) => {
    let total = 0;
    const missing = [];
    priced.forEach((x) => {
      const p = pricesByItem[x.id][st.id];
      if (p) total += Number(p.price) * x.qty;
      else missing.push(x);
    });
    return { store: st, total, available: priced.length - missing.length, missing };
  });

  // הזולה — מבין החנויות שיש בהן הכי הרבה מהרשימה (אחרת חנות שחסרים בה מוצרים נראית זולה סתם)
  const maxAvailable = Math.max(0, ...perStore.map((x) => x.available));
  const candidates = perStore.filter((x) => x.available === maxAvailable && maxAvailable > 0);
  const cheapest = candidates.length > 1 ? candidates.reduce((a, b) => (b.total < a.total ? b : a)) : candidates[0] || null;

  // פיצול: כל מוצר בחנות שבה הוא הכי זול (שוויון → החנות הראשונה)
  const split = {};
  let splitTotal = 0;
  priced.forEach((x) => {
    const options = stores.map((st) => ({ st, p: pricesByItem[x.id][st.id] })).filter((o) => o.p);
    const best = options.reduce((a, b) => (Number(b.p.price) < Number(a.p.price) ? b : a));
    const cost = Number(best.p.price) * x.qty;
    splitTotal += cost;
    if (!split[best.st.id]) split[best.st.id] = { store: best.st, items: [], total: 0 };
    split[best.st.id].items.push(x);
    split[best.st.id].total += cost;
  });

  const cheapestIsComplete = cheapest && cheapest.available === priced.length;
  const savings = cheapestIsComplete ? cheapest.total - splitTotal : 0;

  return { priced, perStore, cheapest, split: Object.values(split), splitTotal, savings, cheapestIsComplete };
};

const s = {
  wrap:        { display: 'flex', flexDirection: 'column', gap: 20 },
  card:        { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 },
  heading:     { margin: '0 0 12px', fontSize: 18, fontWeight: 700 },
  row:         { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input:       { flex: '1 1 240px', padding: '10px 12px', fontSize: 16, border: '1px solid #d0d7de', borderRadius: 10 },
  button:      { padding: '10px 18px', fontSize: 15, fontWeight: 600, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
  pickerHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  importBtn:   { padding: '8px 14px', fontSize: 14, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c' },
  results:     { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 },
  more:        { alignSelf: 'center', padding: '8px 16px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', color: '#1f2328' },
  listItem:    { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f3f3' },
  listName:    { flex: 1, minWidth: 0 },
  thumb:       { width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 8, overflow: 'hidden' },
  thumbSmall:  { width: 32, height: 32, borderRadius: 6 },
  thumbImg:    { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  tdProduct:   { display: 'flex', alignItems: 'center', gap: 8 },
  qty:         { display: 'flex', alignItems: 'center', gap: 6 },
  qtyBtn:      { width: 30, height: 30, borderRadius: 8, border: '1px solid #d0d7de', background: '#fff', fontSize: 18, lineHeight: 1 },
  qtyValue:    { minWidth: 52, textAlign: 'center', fontWeight: 600 },
  remove:      { background: 'none', border: 'none', color: '#8c959f', fontSize: 20, lineHeight: 1, padding: '0 4px' },
  listFooter:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 14, color: '#59636e' },
  doneBtn:     { padding: '6px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 8, background: '#fff', color: '#1f2328' },
  addedBy:     { fontSize: 12, color: '#8c959f', marginTop: 2 },
  errorX:      { background: 'none', border: 'none', color: 'inherit', fontSize: 18, lineHeight: 1, padding: 0 },
  shareBar:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 14px' },
  shareInfo:   { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#59636e' },
  avatars:     { display: 'inline-flex', paddingInlineStart: 6 },
  avatar:      { width: 26, height: 26, marginInlineStart: -6, borderRadius: '50%', border: '2px solid #fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1a3f9c', color: '#fff', fontSize: 11, fontWeight: 700 },
  shareActions:{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  shareBtn:    { padding: '7px 12px', fontSize: 14, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c' },
  waBtn:       { padding: '7px 12px', fontSize: 14, fontWeight: 600, borderRadius: 10, background: '#25d366', color: '#fff', textDecoration: 'none' },
  leaveBtn:    { background: 'none', border: 'none', padding: 0, color: '#cf222e', fontSize: 13, fontWeight: 600 },
  quick:       { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fffbea', border: '1px solid #f5e0a3', borderRadius: 12, padding: '10px 14px' },
  quickTitle:  { fontWeight: 700, fontSize: 14 },
  quickBtn:    { padding: '6px 12px', fontSize: 14, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 20, background: '#fff', color: '#1f2328' },
  quickLink:   { background: 'none', border: 'none', padding: 0, color: '#1a3f9c', fontSize: 13, fontWeight: 600 },
  quickNote:   { fontSize: 13, fontWeight: 600, color: '#1a7f37' },
  stores:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 },
  storeCard:   { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 },
  storeBest:   { borderColor: '#2da44e', background: '#effaf2' },
  storeHead:   { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, color: '#59636e' },
  badge:       { fontSize: 12, fontWeight: 700, color: '#1a7f37' },
  total:       { fontSize: 26, fontWeight: 700, margin: '4px 0' },
  note:        { fontSize: 13, color: '#59636e' },
  missing:     { fontSize: 12, color: '#9a6700', marginTop: 4 },
  splitCard:   { background: '#eef4ff', border: '1px solid #b6ccf5', borderRadius: 12, padding: 16 },
  splitHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 },
  splitTotal:  { fontSize: 26, fontWeight: 700 },
  savings:     { fontSize: 14, fontWeight: 700, color: '#1a7f37' },
  splitList:   { margin: '10px 0 0', paddingInlineStart: 18, color: '#1f2328', lineHeight: 1.7 },
  tableWrap:   { overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th:          { textAlign: 'right', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap', color: '#59636e' },
  td:          { padding: '8px 10px', borderBottom: '1px solid #f3f3f3', whiteSpace: 'nowrap' },
  tdBest:      { color: '#1a7f37', fontWeight: 700 },
  tdMissing:   { color: '#8c959f' },
  empty:       { textAlign: 'center', color: '#59636e', padding: '32px 16px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  error:       { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10 },
};

// תמונה קטנה ברשימה. הכתובת מהפלטפורמה היא תבנית (ר' imageSrc ב-ProductRow.jsx); בלי תמונה — ריבוע ריק באותו גודל
// small — בטבלת הפירוט
const Thumb = ({ url, small = false }) => {
  const [failed, setFailed] = useState(false);
  const src = url ? url.replace('{{size}}', 'medium').replace(/\{\{extension[^}]*\}\}/, 'jpg') : null;
  return (
    <span style={{ ...s.thumb, ...(small ? s.thumbSmall : null) }}>
      {src && !failed && <img style={s.thumbImg} src={src} alt="" loading="lazy" onError={() => setFailed(true)} />}
    </span>
  );
};

// ── הוספת מוצר לרשימה: אותה חוויה כמו החיפוש הראשי (השלמה אוטומטית, תמונות, מחירים, כפתור "לרשימת קניות") ──
const ProductPicker = () => {
  const suggestionItems = useSearchSuggestions();
  const [q, setQ] = useState('');
  const [searched, setSearched] = useState('');
  const [result, setResult] = useState(null);   // { stores, items, pricesByItem }
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const closeImport = useCallback(() => setImportOpen(false), []);
  const requestId = useRef(0);

  const search = async (text) => {
    const query = text.trim();
    if (query.length < 2) { setError('נא להזין לפחות 2 תווים לחיפוש'); return; }
    const id = ++requestId.current;
    setSearched(query);
    setLoading(true);
    setError(null);
    try {
      const resp = await API.searchProducts(query, 'relevance');
      if (id !== requestId.current) return;
      if (resp?.[0]?.[0]?.code === 0) {
        const pricesByItem = {};
        (resp[3] || []).forEach((p) => {
          if (!pricesByItem[p.itemId]) pricesByItem[p.itemId] = {};
          pricesByItem[p.itemId][p.storeId] = p;
        });
        setResult({ stores: resp[1] || [], items: resp[2] || [], pricesByItem });
        setShowAll(false);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בחיפוש');
      }
    } catch (err) {
      if (id === requestId.current) setError(err.message || 'שגיאה בחיפוש');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // ניקוי השדה מסתיר את התוצאות
  const changeQuery = (text) => {
    setQ(text);
    if (text.trim() === '') { ++requestId.current; setResult(null); setLoading(false); setError(null); }
  };

  const shown = result ? (showAll ? result.items : result.items.slice(0, PICKER_RESULTS)) : [];

  return (
    <div style={s.card}>
      <div style={s.pickerHead}>
        <h2 style={{ ...s.heading, margin: 0 }}>הוספת מוצר</h2>
        <button type="button" style={s.importBtn} onClick={() => setImportOpen(true)}>📋 טעינת רשימה מוכנה</button>
      </div>
      <div style={s.row}>
        <SearchBox value={q} onChange={changeQuery} onSubmit={search} items={suggestionItems} />
        <button type="button" style={s.button} onClick={() => search(q)} disabled={loading}>{loading ? 'מחפש…' : 'חיפוש'}</button>
      </div>
      {error && <div style={{ ...s.error, marginTop: 10 }}>{error}</div>}
      {result && (
        result.items.length === 0 ? (
          <div style={{ ...s.note, marginTop: 10 }}>לא נמצאו מוצרים עבור "{searched}"</div>
        ) : (
          <div style={s.results}>
            {shown.map((item) => (
              <ProductRow key={item.id} item={item} stores={result.stores} prices={result.pricesByItem[item.id] || {}} />
            ))}
            {result.items.length > shown.length && (
              <button type="button" style={s.more} onClick={() => setShowAll(true)}>
                הצגת כל {result.items.length} התוצאות
              </button>
            )}
          </div>
        )
      )}
      {importOpen && <ImportListDialog onClose={closeImport} />}
    </div>
  );
};

// הרשימה כטקסט לוואטסאפ: המוצרים והכמויות, והמחיר בכל חנות (אם כבר חושב)
const whatsappText = (items, comparison) => {
  const lines = ['🛒 רשימת קניות — זול בכפר', ''];
  items.forEach((x) => lines.push(`• ${x.name}${x.isWeighable ? ` — ${x.qty} ק"ג` : x.qty !== 1 ? ` ×${x.qty}` : ''}`));
  if (comparison && comparison.perStore.length > 1 && comparison.priced.length > 0) {
    lines.push('');
    comparison.perStore.forEach((x) => lines.push(`${x.store.name}: ${formatPrice(x.total)}${comparison.cheapest?.store.id === x.store.id ? ' ✅ הכי זול' : ''}`));
  }
  lines.push('', window.location.origin);
  return lines.join('\n');
};

const formatShortDate = (v) => { const d = new Date(v); return `${d.getDate()}.${d.getMonth() + 1}`; };

const BasketTab = ({ active }) => {
  const items = useBasket();
  const meta = useBasketMeta();
  const favoriteIds = useFavoriteIds();
  const [history, setHistory] = useState(null);     // { archives, itemsByArchive } — "רשימות קודמות"
  const [dialog, setDialog] = useState(null);       // 'share' | 'history'
  const closeDialog = useCallback(() => setDialog(null), []);
  const [quickNote, setQuickNote] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const resp = await API.getShoppingListHistory();
      if (resp?.[0]?.[0]?.code !== 0) return;
      const itemsByArchive = {};
      (resp[2] || []).forEach((x) => { (itemsByArchive[x.archiveId] = itemsByArchive[x.archiveId] || []).push(x); });
      setHistory({ archives: resp[1] || [], itemsByArchive });
    } catch (e) { /* בלי הוספה מרשימה קודמת */ }
  }, []);

  // בכל פתיחה של הטאב — מתעדכנים (גם רשימה משותפת שמישהו אחר שינה / סיים)
  const listId = meta.list?.id;
  useEffect(() => {
    if (active && meta.loaded) { loadHistory(); basket.refresh(); }
  }, [active, meta.loaded, listId, loadHistory]);

  const showNote = (text) => { setQuickNote(text); setTimeout(() => setQuickNote(null), 3500); };

  const addFavorites = async () => {
    const count = await basket.addFavorites();
    if (count !== null) showNote(count ? `נוספו ${count} מוצרים מהמועדפים` : 'כל המועדפים כבר ברשימה');
  };

  const addLastList = async () => {
    const last = history?.archives?.[0];
    if (!last) return;
    const count = await basket.addArchive(last.id);
    if (count !== null) showNote(count ? `נוספו ${count} מוצרים מהרשימה הקודמת` : 'הכמויות מהרשימה הקודמת נוספו');
  };

  const leaveShared = async () => {
    if (!window.confirm('לצאת מהרשימה המשותפת? הרשימה תישאר לשאר בני הבית, ואת/ה תתחיל/י רשימה חדשה.')) return;
    await basket.leave();
    loadHistory();
  };
  const [data, setData] = useState(null);     // { stores, pricesByItem }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  // המחירים נטענים מחדש רק כשרשימת המוצרים משתנה (לא בשינוי כמות)
  const idsKey = items.map((x) => x.id).sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!items.length) { setData(null); return undefined; }
    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await API.compareBasket(basket.getItems());
        if (id !== requestId.current) return;
        if (resp?.[0]?.[0]?.code === 0) {
          const pricesByItem = {};
          (resp[3] || []).forEach((p) => {
            if (!pricesByItem[p.itemId]) pricesByItem[p.itemId] = {};
            pricesByItem[p.itemId][p.storeId] = p;
          });
          // פרטי המוצרים — לתמונה של מוצרים שנוספו לרשימה לפני ששמרנו בה imageUrl
          const infoById = {};
          (resp[2] || []).forEach((it) => { infoById[it.id] = it; });
          setData({ stores: resp[1] || [], pricesByItem, infoById });
        } else {
          setError(resp?.[0]?.[0]?.msg || 'שגיאה בהשוואה');
        }
      } catch (err) {
        if (id === requestId.current) setError(err.message || 'שגיאה בהשוואה');
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, COMPARE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparison = useMemo(
    () => (data ? computeComparison(items, data.stores, data.pricesByItem) : null),
    [items, data],
  );

  // "סיימתי": הרשימה נשמרת ב"רשימות קודמות" ומתרוקנת (spClearShoppingList)
  const clearAll = async () => {
    if (!window.confirm('לסיים ולרוקן את הרשימה? היא תישמר ב"רשימות קודמות", ואפשר להוסיף אותה שוב בלחיצה.')) return;
    await basket.clear();
    loadHistory();
  };

  const others = meta.members.filter((m) => !m.isMe);
  const lastList = history?.archives?.[0];

  const notFound = data ? items.filter((x) => !data.pricesByItem[x.id]) : [];

  return (
    <div style={s.wrap}>
      {/* שיתוף ושליחה */}
      <div style={s.shareBar}>
        <div style={s.shareInfo}>
          {others.length > 0 ? (
            <>
              <span style={s.avatars} aria-hidden="true">
                {meta.members.map((m) => <span key={m.userId} style={s.avatar}>{m.firstName?.[0]}{m.lastName?.[0]}</span>)}
              </span>
              <span>רשימה משותפת עם {others.map((m) => m.firstName).join(', ')}</span>
            </>
          ) : (
            <span>הרשימה נשמרת בחשבון שלך — זמינה בכל מכשיר</span>
          )}
        </div>
        <div style={s.shareActions}>
          <button type="button" style={s.shareBtn} onClick={() => setDialog('share')}>👨‍👩‍👧 {others.length ? 'הזמנת בני בית' : 'שיתוף עם בני הבית'}</button>
          {items.length > 0 && (
            <a style={s.waBtn} href={`https://wa.me/?text=${encodeURIComponent(whatsappText(items, comparison))}`} target="_blank" rel="noopener noreferrer">
              📤 שליחה בוואטסאפ
            </a>
          )}
          {others.length > 0 && <button type="button" style={s.leaveBtn} onClick={leaveShared}>יציאה מהרשימה המשותפת</button>}
        </div>
      </div>

      {meta.error && (
        <div style={{ ...s.error, display: 'flex', justifyContent: 'space-between', gap: 10 }} role="alert">
          <span>{meta.error}</span>
          <button type="button" style={s.errorX} onClick={basket.clearError} aria-label="סגירה">×</button>
        </div>
      )}

      <ProductPicker />

      {/* הוספה מהירה */}
      {meta.loaded && (favoriteIds.size > 0 || lastList) && (
        <div style={s.quick}>
          <span style={s.quickTitle}>⚡ הוספה מהירה:</span>
          {favoriteIds.size > 0 && (
            <button type="button" style={s.quickBtn} onClick={addFavorites}>❤️ כל המועדפים ({favoriteIds.size})</button>
          )}
          {lastList && (
            <button type="button" style={s.quickBtn} onClick={addLastList}>
              🕘 הרשימה הקודמת ({lastList.itemCount} מוצרים, {formatShortDate(lastList.iTime)})
            </button>
          )}
          {history?.archives?.length > 0 && (
            <button type="button" style={s.quickLink} onClick={() => setDialog('history')}>כל הרשימות הקודמות</button>
          )}
          {quickNote && <span style={s.quickNote} role="status">✓ {quickNote}</span>}
        </div>
      )}

      {!meta.loaded ? (
        <div style={s.empty}>טוען את רשימת הקניות…</div>
      ) : items.length === 0 ? (
        <div style={s.empty}>
          רשימת הקניות ריקה.<br />
          אפשר לחפש מוצרים כאן למעלה, להדביק רשימה מוכנה ב"📋 טעינת רשימה מוכנה",<br />
          או להוסיף בכפתור "➕ לרשימת קניות" ליד כל מוצר באתר.
        </div>
      ) : (
        <>
          {/* הרשימה */}
          <div style={s.card}>
            <h2 style={s.heading}>הרשימה שלי ({items.length})</h2>
            {items.map((x) => (
              <div key={x.id} style={s.listItem}>
                <Thumb url={x.imageUrl || data?.infoById?.[x.id]?.imageUrl} />
                <div style={s.listName}>
                  {x.name}
                  {x.addedByName && <div style={s.addedBy}>הוסיף/ה: {x.addedByName}</div>}
                  {notFound.some((n) => n.id === x.id) && <div style={s.missing}>לא נמכר כרגע באף חנות</div>}
                </div>
                <div style={s.qty}>
                  <button type="button" style={s.qtyBtn} onClick={() => basket.setQty(x.id, x.qty - qtyStep(x))} disabled={x.qty <= minQty(x)} aria-label="פחות">−</button>
                  <span style={s.qtyValue} className="num">{formatQty(x)}</span>
                  <button type="button" style={s.qtyBtn} onClick={() => basket.setQty(x.id, x.qty + qtyStep(x))} aria-label="יותר">+</button>
                </div>
                <button type="button" style={s.remove} onClick={() => basket.remove(x.id)} aria-label={`הסרת ${x.name}`}>×</button>
              </div>
            ))}
            <div style={s.listFooter}>
              <span>{loading ? 'מעדכן מחירים…' : ''}</span>
              <button type="button" style={s.doneBtn} onClick={clearAll}>✅ סיימתי — ריקון הרשימה</button>
            </div>
          </div>

          {error && <div style={s.error}>{error}</div>}

          {comparison && comparison.priced.length > 0 && (
            <>
              {/* כל הרשימה בחנות אחת */}
              <div>
                <h2 style={s.heading}>כל הרשימה בחנות אחת</h2>
                <div style={s.stores}>
                  {comparison.perStore.map((x) => {
                    const isBest = comparison.cheapest && comparison.cheapest.store.id === x.store.id && comparison.perStore.length > 1;
                    return (
                      <div key={x.store.id} style={{ ...s.storeCard, ...(isBest ? s.storeBest : null) }}>
                        <div style={s.storeHead}>
                          <span>{x.store.name}</span>
                          {isBest && <span style={s.badge}>הכי זול</span>}
                        </div>
                        <div style={s.total} className="num">{formatPrice(x.total)}</div>
                        <div style={s.note}>{x.available} מתוך {comparison.priced.length} מוצרים</div>
                        {x.missing.length > 0 && (
                          <div style={s.missing}>חסר: {x.missing.map((m) => m.name).join(', ')}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* פיצול בין החנויות */}
              {comparison.split.length > 1 && (
                <div style={s.splitCard}>
                  <div style={s.splitHead}>
                    <h2 style={{ ...s.heading, margin: 0 }}>הכי משתלם: לקנות כל מוצר איפה שהוא הכי זול</h2>
                    <span style={s.splitTotal} className="num">{formatPrice(comparison.splitTotal)}</span>
                  </div>
                  {comparison.savings > 0.009 && (
                    <div style={s.savings}>חיסכון של {formatPrice(comparison.savings)} לעומת הכל ב{comparison.cheapest.store.name}</div>
                  )}
                  <ul style={s.splitList}>
                    {comparison.split.map((g) => (
                      <li key={g.store.id}>
                        <b>{g.store.name}</b> ({formatPrice(g.total)}): {g.items.map((m) => m.name).join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* פירוט לפי מוצר */}
              <div style={s.card}>
                <h2 style={s.heading}>פירוט לפי מוצר</h2>
                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>מוצר</th>
                        <th style={s.th}>כמות</th>
                        {data.stores.map((st) => <th key={st.id} style={s.th}>{st.name}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.priced.map((x) => {
                        const row = data.pricesByItem[x.id];
                        const min = Math.min(...Object.values(row).map((p) => Number(p.price)));
                        const several = Object.keys(row).length > 1;
                        return (
                          <tr key={x.id}>
                            <td style={s.td}>
                              <span style={s.tdProduct}>
                                <Thumb url={x.imageUrl || data.infoById?.[x.id]?.imageUrl} small />
                                {x.name}
                              </span>
                            </td>
                            <td style={s.td} className="num">{formatQty(x)}</td>
                            {data.stores.map((st) => {
                              const p = row[st.id];
                              if (!p) return <td key={st.id} style={{ ...s.td, ...s.tdMissing }}>—</td>;
                              const isMin = several && Number(p.price) === min;
                              return (
                                <td key={st.id} style={{ ...s.td, ...(isMin ? s.tdBest : null) }} className="num"
                                    title={x.qty !== 1 ? `${formatPrice(p.price)} × ${x.qty}` : undefined}>
                                  {formatPrice(Number(p.price) * x.qty)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
      {dialog === 'share' && <ShareListDialog onClose={closeDialog} />}
      {dialog === 'history' && <ListHistoryDialog history={history} onClose={closeDialog} />}
    </div>
  );
};

export default BasketTab;
